import {
  SenderNotification,
  DoneNotification,
  SupervisorNotification,
  PasswordNotification,
  Supervisor,
  EmailExpiration,
  Reminder,
  LinkFormat,
  Content,
  Storage,
} from "@dashboard-v3/api";
import {
  typePath,
  getCloudStorage,
  getContentToSave,
  getContentFilter,
  getEmailFlow,
  getArchivingAddress,
  getMailbox,
  getStorageWebhook,
} from "pages/Rules/Form/context/ruleSelectors";

import { Rule } from "pages/Rules/common/types";

type Dict = Record<string, unknown>;
type ActionData = ReturnType<typeof typePath>;

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Returns a copy without the empty values, or null when nothing is left — a
 * section whose fields are all empty is not reported at all.
 */
function dropEmptyValues(values: Dict): Dict | null {
  const kept: Dict = {};
  for (const [key, value] of Object.entries(values)) {
    if (isEmptyValue(value)) continue;
    kept[key] = value;
  }
  return Object.keys(kept).length ? kept : null;
}

/** The branch of the rule that holds the data for its action type. */
const getActionData = (rule: Rule): ActionData =>
  typePath(rule.actionType, rule);

function readActionField<T>(data: ActionData, key: string): T | undefined {
  return data && key in data ? ((data as Dict)[key] as T) : undefined;
}

/**
 * Writes a field, failing outside production when the name is already taken.
 * Overwriting would silently drop the change the first write carried.
 */
function assignUnique(target: Dict, key: string, value: unknown) {
  if (key in target && process.env.NODE_ENV !== "production") {
    throw new Error(
      `ConfirmUpdateModal: two sources produced "${key}" in the same section. ` +
        `Prefix one of them with the name of its source object.`
    );
  }
  target[key] = value;
}

/** Spreads a notification's fields as `<prefix>Title`, `<prefix>BodyText`, … */
function flattenNotification(
  target: Dict,
  prefix: string,
  notification: Dict | undefined,
  fields: string[]
) {
  if (!notification) return;
  for (const field of fields) {
    const key = `${prefix}${field[0].toUpperCase()}${field.slice(1)}`;
    assignUnique(target, key, notification[field]);
  }
}

const SENDER_FIELDS = [
  "title",
  "bodyText",
  "accessLink",
  "accessLinkLabel",
  "cancelLink",
  "cancelLinkLabel",
];
const SUPERVISOR_FIELDS = [
  "title",
  "subject",
  "bodyText",
  "accessLink",
  "accessLinkLabel",
  "approveLink",
  "approveLinkLabel",
  "rejectLink",
  "rejectLinkLabel",
];

export function selectRuleLabels(rule: Rule) {
  return dropEmptyValues({
    name: rule.name,
    description: rule.description,
    locale: rule.locale,
    timeZone: rule.timeZone,
  });
}

export function selectActionStep(rule: Rule) {
  const data = getActionData(rule);
  if (!data) return null;

  const contentToSave = getContentToSave(rule);
  const contentTypes = contentToSave?.content?.map(c => c.type) ?? [];

  const out: Dict = {
    contentTypes,
    linksFormat: mapLinkFormat(
      readActionField<LinkFormat[]>(data, "linksFormat")
    ),
    storageNotificationTemplateId:
      getCloudStorage(rule)?.storageNotificationTemplateId,
    passwordNotificationToRecipient: readActionField<boolean>(
      data,
      "passwordNotificationToRecipient"
    ),
    passwordNotificationToSender: readActionField<boolean>(
      data,
      "passwordNotificationToSender"
    ),
  };

  const block = readActionField<Dict>(data, "blockNotification");
  if (block) {
    out.blockTitle = block.title;
    out.blockBodyText = block.bodyText;
  }

  const sender = readActionField<Dict>(data, "senderNotification");
  if (sender) {
    out.senderTitle = sender.title;
    out.senderBodyText = sender.bodyText;
  }

  const password = readActionField<PasswordNotification>(
    data,
    "passwordNotificationText"
  );
  if (password) {
    out.passwordRecipientLineText = password.recipientLineText;
    out.passwordSubjectTag = password.subjectTag;
    out.passwordSenderLineText = password.senderLineText;
    out.passwordSenderAllLineText = password.senderAllLineText;
  }

  return dropEmptyValues(out);
}

export function selectActionFilter(rule: Rule) {
  const filter = getContentFilter(rule);
  if (!filter) return null;

  return dropEmptyValues({
    attachmentSize: filter.attachmentSize,
    excludeAttachmentExtensions: filter.excludeAttachmentExtensions ?? [],
    includeAttachmentExtensions: filter.includeAttachmentExtensions ?? [],
    excludeAttachmentNames: filter.excludeAttachmentNames ?? [],
    includeAttachmentNames: filter.includeAttachmentNames ?? [],
    excludeAttachmentTypes: filter.excludeAttachmentTypes ?? [],
    includeAttachmentTypes: filter.includeAttachmentTypes ?? [],
    includeRfc822: filter.includeRfc822,
    excludeInlineImages: filter.excludeInlineImages,
    includeZipWithPassword: filter.includeZipWithPassword,
    excludeMultipartReport: filter.excludeMultipartReport,
    filterPolicyId: filter.filterPolicyId,
    includeTnef: filter.includeTnef,
  });
}

export function selectTargetStep(rule: Rule) {
  if (rule.targetType === "EMAIL_FLOW") {
    const ef = getEmailFlow(rule);
    if (!ef) return null;
    const data = getActionData(rule);

    return dropEmptyValues({
      bidirectional: ef.bidirectional,
      evaluateHeaders: ef.evaluateHeaders,
      evaluateFrom: ef.evaluateFrom,
      smtpPhase: ef.smtpPhase,
      fromDirection: ef.fromTo?.fromDirection,
      toDirection: ef.fromTo?.toDirection,
      syncAllByRule: getCloudStorage(rule)?.syncAllByRule,
      passwordPerRecipient: readActionField<boolean>(
        data,
        "passwordPerRecipient"
      ),
      saveForwardedMessages: readActionField<boolean>(
        data,
        "saveForwardedMessages"
      ),
    });
  }

  if (rule.targetType === "ARCHIVING_ADDRESS") {
    const aa = getArchivingAddress(rule);
    if (!aa) return null;

    return dropEmptyValues({
      emailAddress: aa.emailAddress,
      redirectOnErrorEmailAddress: aa.redirectOnErrorEmailAddress,
      saveForwardedMessages: aa.saveForwardedMessages,
    });
  }

  if (rule.targetType === "MAIL_BOX") {
    const mb = getMailbox(rule);
    if (!mb) return null;

    return dropEmptyValues({
      imapHost: mb.imapServer?.host,
      imapUsername: mb.imapServer?.username,
      imapPassword: mb.imapServer?.password,
      imapPort: mb.imapServer?.port,
      imapSsl: mb.imapServer?.ssl,
      imapAuthMechanism: mb.imapServer?.authenticationMechanism,
      notificationOnDone: mb.notifications?.onDone,
      notificationOnError: mb.notifications?.onError,
      notificationEmail: mb.notifications?.email,
      postProcessingMarkProcessed: mb.postProcessing?.markProcessed,
      postProcessingMarkProcessedText: mb.postProcessing?.markProcessedText,
      copyEmailFilesWhenNoAttachments: mb.copyEmailFilesWhenNoAttachments,
    });
  }

  if (rule.targetType === "STORAGE_WEBHOOK") {
    const sw = getStorageWebhook(rule);
    if (!sw) return null;

    return dropEmptyValues({ folders: sw.folders ?? [] });
  }

  return null;
}

export function selectTargetFilter(rule: Rule) {
  if (rule.targetType === "EMAIL_FLOW") {
    const ef = getEmailFlow(rule);
    const filter = ef?.filter;

    return dropEmptyValues({
      filterPolicyId: rule.filterPolicyId,
      cancelShortCodeEnabled: filter?.cancelShortCodeEnabled,
      onlyManagedAccounts: filter?.onlyManagedAccounts,
      triggerOnShortCode: filter?.triggerOnShortCode,
      excludeMatching: filter?.excludeMatching,
      exclusions: filter?.exclude ?? [],
    });
  }

  if (rule.targetType === "ARCHIVING_ADDRESS") {
    const aa = getArchivingAddress(rule);

    return dropEmptyValues({
      filterPolicyId: rule.filterPolicyId,
      // The form toggles this on and off, always writing the same list, so
      // the switch is what changed and not its contents.
      filterBySender: Boolean(aa?.filter?.filterBySender),
    });
  }

  return null;
}

export function selectCloudStorage(rule: Rule) {
  const storage = getCloudStorage(rule);
  if (!storage) return null;

  return dropEmptyValues({
    // The account select writes provider, authentication and userId at once,
    // so they travel as one field and read as one row.
    storageAccount: { provider: storage.provider, userId: storage.userId },
    userIds: storage.userIds ?? [],
    asUser: storage.asUser,
    asUsers: storage.asUsers ?? [],
    storeId: storage.storeId,
    metadata: storage.metadata,
    trackingType: storage.trackingType,
    allManagedUserIds: storage.allManagedUserIds,
    folderSharing: storage.folderSharing,
    linkSecurity: storage.linkSecurity,
    retention: storage.retention || "DEFAULT",
  });
}

export function selectStorageFilepath(rule: Rule) {
  const storage = getCloudStorage(rule);
  if (!storage) return null;

  // EML, MSG and ORIGINAL are the file types the same "saved email" field
  // can take, so one property covers the three.
  const findNameFormat = (...types: Content["type"][]) =>
    getContentToSave(rule)?.content?.find(c => types.includes(c.type))
      ?.nameFormat;

  return dropEmptyValues({
    override: storage.override,
    // The three fields make up the single path the form displays.
    filepath: buildFilepath(storage.location),
    copyPolicy: storage.location?.copyPolicy,
    bodySubfolder: storage.location?.bodySubfolder,
    emailSubfolder: storage.location?.emailSubfolder,
    attachmentsSubfolder: storage.location?.attachmentsSubfolder,
    collaboration: storage.collaboration,
    nameFormatEmail: findNameFormat("EML", "MSG", "ORIGINAL"),
    nameFormatAttachments: findNameFormat("ATTACHMENTS"),
    nameFormatPdf: findNameFormat("PDF"),
  });
}

function buildFilepath(location: Storage["location"] | undefined) {
  const { parentId, parentName, folderStructure } = location ?? {};
  if (!parentId && !parentName && !folderStructure) return undefined;
  return { parentId, parentName, folderStructure };
}

export function selectStorageActions(rule: Rule) {
  const storage = getCloudStorage(rule);
  if (!storage) return null;

  const out: Dict = {};

  const onError = storage.onStorageError?.emailAction;
  if (onError) {
    out.onErrorType = onError.type;
    if ("recipientAddress" in onError) {
      out.onErrorRecipient = onError.recipientAddress;
    }
  }

  const onSuccess = storage.onStorageSuccess?.emailAction;
  if (onSuccess) {
    out.onSuccessType = onSuccess.type;
    out.onSuccessContent = onSuccess.originalEmail ? "ORIGINAL" : "PROCESSED";
    if ("recipientAddress" in onSuccess) {
      out.onSuccessRecipient = onSuccess.recipientAddress;
    }
    if ("variableId" in onSuccess) {
      out.onSuccessVariable = onSuccess.variableId;
    }
  }

  const aiAction = storage.onStorageSuccess?.aiAction;
  if (aiAction) out.aiActionScope = aiAction.scope;

  return dropEmptyValues(out);
}

export function selectSendDelay(rule: Rule) {
  if (rule.actionType !== "SEND_DELAY") return null;
  const data = getActionData(rule);
  if (!data) return null;

  const out: Dict = {
    expirationValue: readActionField<EmailExpiration>(data, "expiration")
      ?.value,
    expirationType: readActionField<EmailExpiration>(data, "expiration")?.type,
    rejectOnExpiration: readActionField<boolean>(data, "rejectOnExpiration"),
    allowStop: readActionField<boolean>(data, "allowStop"),
    reminderValue: readActionField<Reminder>(data, "reminder")?.value,
    reminderType: readActionField<Reminder>(data, "reminder")?.type,
  };

  flattenNotification(
    out,
    "sender",
    readActionField<SenderNotification>(data, "senderNotification"),
    SENDER_FIELDS
  );
  flattenNotification(
    out,
    "reminderSender",
    readActionField<SenderNotification>(data, "reminderSenderNotification"),
    SENDER_FIELDS
  );

  const done = readActionField<DoneNotification>(data, "doneNotification");

  if (done) {
    out.doneTitle = done.title;
    out.doneApprovedBodyText = done.approvedBodyText;
    out.doneRejectedBodyText = done.rejectedBodyText;
    out.doneCanceledBodyText = done.canceledBodyText;
  }

  return dropEmptyValues(out);
}

export function selectApprovalSteps(rule: Rule) {
  if (rule.actionType !== "REQUEST_APPROVAL") return null;
  const data = getActionData(rule);
  if (!data) return null;

  const out: Dict = {
    supervisors: readActionField<Supervisor[]>(data, "supervisors") ?? [],
    expirationValue: readActionField<EmailExpiration>(data, "expiration")
      ?.value,
    expirationType: readActionField<EmailExpiration>(data, "expiration")?.type,
    reminderValue: readActionField<Reminder>(data, "reminder")?.value,
    reminderType: readActionField<Reminder>(data, "reminder")?.type,
  };

  flattenNotification(
    out,
    "sender",
    readActionField<SenderNotification>(data, "senderNotification"),
    SENDER_FIELDS
  );
  flattenNotification(
    out,
    "reminderSender",
    readActionField<SenderNotification>(data, "reminderSenderNotification"),
    SENDER_FIELDS
  );
  flattenNotification(
    out,
    "supervisor",
    readActionField<SupervisorNotification>(data, "supervisorNotification"),
    SUPERVISOR_FIELDS
  );

  const done = readActionField<DoneNotification>(data, "doneNotification");
  if (done) {
    out.doneTitle = done.title;
    out.doneApprovedBodyText = done.approvedBodyText;
    out.doneRejectedBodyText = done.rejectedBodyText;
  }

  return dropEmptyValues(out);
}

export function selectSmimeActions(rule: Rule) {
  if (rule.actionType !== "SMIME_EMAILS") return null;

  const data = getActionData(rule) as {
    smimeActions?: string[];
    failedAction?: {
      subjectInvalidSmime?: string;
      addSubjectInvalidSmime?: boolean;
    };
  };

  if (!data) return null;

  return dropEmptyValues({
    smimeActions: data.smimeActions ?? [],
    addSubjectInvalidSmime: data.failedAction?.addSubjectInvalidSmime,
    subjectInvalidSmime: data.failedAction?.subjectInvalidSmime,
  });
}

function mapLinkFormat(
  linksFormat: LinkFormat[] | undefined
): "PDF" | "LINK" | "BOTH" | undefined {
  if (!linksFormat) return undefined;
  if (linksFormat.length === 0) return "PDF";
  if (linksFormat.length > 1) return "BOTH";
  return linksFormat[0];
}
